import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from '@/shared/api'
import { type CursorPage, nextCursor } from '@/shared/lib'
import { ProjectCardSchema, ProjectReadSchema, ProjectSummarySchema } from '../model/types'

// Пресеты окна срока шага: значения внутри контракта бэка (`stage_deadline`).
export const STAGE_DEADLINE_PRESETS = ['overdue', 'today', '7d', '30d', 'none'] as const
export type StageDeadlinePreset = (typeof STAGE_DEADLINE_PRESETS)[number]

// Фильтры канбана. Наборы (company_id/responsible_id/tag/priority) уходят одним
// ключом через запятую: значения внутри набора — по ИЛИ, разные фильтры — по И.
// Пресет срока и произвольный диапазон взаимоисключающи (вместе бэк даёт 400).
export interface BoardFilters {
  // Воронка, чей канбан показываем. Пусто — карточки всех воронок.
  board_id?: string
  company_id?: string[]
  responsible_id?: string[]
  // Точные теги (case-insensitive, нормализуются на бэке).
  tag?: string[]
  // Приоритеты 1..10.
  priority?: number[]
  column_id?: string
  stage_deadline?: StageDeadlinePreset
  // Границы включительны, формат `YYYY-MM-DD`.
  stage_deadline_from?: string
  stage_deadline_to?: string
  // Показать скрытые в deal проекты (visibility service=deal). По умолчанию бэк их прячет.
  include_hidden?: boolean
  // Показать архивные проекты (по умолчанию доска их не отдаёт).
  include_archived?: boolean
}

// Фильтры ленты проектов компании — те же include-флаги, что у справочника
// компаний: узкая выборка по умолчанию, полная — под срезы «архив»/«скрытые».
export interface CompanyProjectFilters {
  include_hidden?: boolean
  include_archived?: boolean
}

export const projectKeys = {
  all: ['project'] as const,
  boardProjects: (filters: BoardFilters) => ['project', 'board', filters] as const,
  companyProjects: (companyId: string, filters: CompanyProjectFilters = {}) =>
    ['project', 'company', companyId, filters] as const,
  detail: (id: string) => ['project', 'detail', id] as const,
  bySlug: (company: string, slug: string) => ['project', 'by-slug', company, slug] as const,
}

// Проекты компании (лента карточки компании у сотрудника + контур заказчика
// /projects — тот же эндпоинт, срез по роли делает бэк).
export function companyProjectsQueryOptions(
  companyId: string,
  filters: CompanyProjectFilters = {},
) {
  return infiniteQueryOptions({
    queryKey: projectKeys.companyProjects(companyId, filters),
    queryFn: async ({ pageParam }) => {
      const body = await apiRequest<CursorPage<unknown>>({
        method: 'GET',
        url: `/companies/${companyId}/projects`,
        query: { ...filters, ...(pageParam ? { cursor: pageParam } : {}), limit: 200 },
      })
      return {
        items: z.array(ProjectSummarySchema).parse(body.items),
        next_cursor: body.next_cursor ?? null,
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  })
}

// Наборы едут одним ключом через запятую (`?priority=1,3`) — так их читает бэк.
// Пустой набор параметра не даёт вовсе.
function setParam(values: readonly (string | number)[] | undefined): string | undefined {
  return values && values.length > 0 ? values.join(',') : undefined
}

// Курсорный листинг карточек воронки. Фильтры — часть ключа; воронка задаёт
// адрес выборки.
export function boardProjectsQueryOptions(filters: BoardFilters = {}) {
  const { board_id: boardId, company_id, responsible_id, tag, priority, ...query } = filters
  return infiniteQueryOptions({
    queryKey: projectKeys.boardProjects(filters),
    queryFn: async ({ pageParam }) => {
      // Воронка ещё не выбрана (список не доехал) — ходить некуда: карточки
      // живут внутри воронки, кросс-воронного листинга нет.
      if (!boardId) return { items: [], next_cursor: null }
      const body = await apiRequest<CursorPage<unknown>>({
        method: 'GET',
        url: `/boards/${boardId}/projects`,
        query: {
          ...query,
          ...(setParam(company_id) ? { company_id: setParam(company_id) } : {}),
          ...(setParam(responsible_id) ? { responsible_id: setParam(responsible_id) } : {}),
          ...(setParam(tag) ? { tag: setParam(tag) } : {}),
          ...(setParam(priority) ? { priority: setParam(priority) } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: 200,
        },
      })
      return {
        items: z.array(ProjectCardSchema).parse(body.items),
        next_cursor: body.next_cursor ?? null,
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  })
}

// Полный проект по id — прямой канал бэка (`GET /api/projects/{id}`).
export function projectDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: projectKeys.detail(id),
    queryFn: async () =>
      ProjectReadSchema.parse(await apiRequest({ method: 'GET', url: `/projects/${id}` })),
  })
}

// Резолв человекочитаемого адреса `/:company/:project` в проект одним запросом. На
// бэке by-slug эндпоинта пока нет (хендофф в roadmap Backlog) — mock реализует.
export function projectBySlugQueryOptions(company: string, slug: string) {
  return queryOptions({
    queryKey: projectKeys.bySlug(company, slug),
    queryFn: async () =>
      ProjectReadSchema.parse(
        await apiRequest({ method: 'GET', url: '/projects/by-slug', query: { company, slug } }),
      ),
  })
}
